import useAuth from 'hooks/useAuth';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { postShiftLogin } from 'store/ducks/Laboratory/service';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface ShiftSessionContextValue {
  /** true enquanto o login no IRIS ainda não foi concluído */
  loading: boolean;
  /** mensagem de erro, ou null */
  error: string | null;
  /**
   * URL da tela principal do IRIS após login.
   * IframeShift cria um <iframe src={mainUrl}> — o cookie CSPSESSIONID
   * já foi setado pelo pre-login, então carrega autenticado diretamente.
   */
  mainUrl: string | null;
}

const ShiftSessionContext = createContext<ShiftSessionContextValue>({
  loading: true,
  error: null,
  mainUrl: null,
});

export const useShiftSession = () => useContext(ShiftSessionContext);

// ---------------------------------------------------------------------------
// Cache de sessão (sessionStorage)
// ---------------------------------------------------------------------------
const CACHE_KEY = 'shift_main_url';
const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 horas

interface CachedSession {
  url: string;
  labId: string;
  at: number;
}

function readCache(labId: string): string | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { url, labId: cachedLab, at } = JSON.parse(raw) as CachedSession;
    if (cachedLab !== labId) return null;
    if (Date.now() - at > CACHE_TTL_MS) return null;
    const hasSession = document.cookie
      .split(';')
      .some(
        (c) =>
          c.trim().startsWith('CSPSESSIONID=') &&
          c.split('=')[1]?.trim().length > 5,
      );
    return hasSession ? url : null;
  } catch {
    return null;
  }
}

function writeCache(labId: string, url: string) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ url, labId, at: Date.now() } as CachedSession),
    );
  } catch { /* empty */ }
}

function clearCache() {
  sessionStorage.removeItem(CACHE_KEY);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const ShiftSessionProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isInitialized } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainUrl, setMainUrl] = useState<string | null>(null);

  // iframe oculto usado APENAS para executar o autologin em background.
  // Nunca é movido para o DOM visível — evita reload causado por appendChild.
  const hiddenIframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox',
    );
    document.body.appendChild(iframe);
    hiddenIframeRef.current = iframe;
    return () => {
      document.body.removeChild(iframe);
    };
  }, []);

  // Recebe o postMessage da autologin page quando o login no IRIS concluiu.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'SHIFT_LOGIN_COMPLETE') return;
      const url: string = event.data.mainUrl ?? '';
      if (url && user?.laboratoryId) {
        writeCache(user.laboratoryId, url);
        setMainUrl(url);
      }
      setLoading(false);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [user?.laboratoryId]);

  // Dispara o pré-login assim que o usuário estiver autenticado.
  useEffect(() => {
    if (!isInitialized) return;

    if (!isAuthenticated || !user?.laboratoryId) {
      clearCache();
      setMainUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    const labId = user.laboratoryId;
    setError(null);
    setLoading(true);

    // Sessão IRIS ainda válida: usa URL em cache diretamente, sem autologin
    const cachedUrl = readCache(labId);
    if (cachedUrl) {
      setMainUrl(cachedUrl);
      setLoading(false);
      return;
    }

    // Sem cache: dispara autologin em background via iframe oculto
    postShiftLogin(labId)
      .then(({ iframeUrl }) => {
        const apiBase = (import.meta.env.VITE_END_POINT ?? '').replace(
          /\/$/,
          '',
        );
        const autologinUrl = `${apiBase}${iframeUrl}`;
        const iframe = hiddenIframeRef.current;
        if (iframe) {
          iframe.src = autologinUrl;
          // loading=false e mainUrl serão definidos pelo handler do postMessage
        }
      })
      .catch((err) => {
        console.error('[ShiftSession] Pré-autenticação falhou:', err);
        setError('Não foi possível autenticar no Shift LIS.');
        setLoading(false);
      });
  }, [isInitialized, isAuthenticated, user?.laboratoryId]);

  return (
    <ShiftSessionContext.Provider value={{ loading, error, mainUrl }}>
      {children}
    </ShiftSessionContext.Provider>
  );
};
